import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { db } from "../firebase";
import { useBusiness } from "../Checkout/useCoinBalance";
import { SERVICE_PCT, clientTotal, isVerifiedBusiness, serviceCharge } from "../Data/offers";
import "./businessCart.css";

// Showroom mode: a verified boutique shows our designs to its own client at
// the counter. Prices stay exactly as on the website (with any discount);
// each one carries a note that the boutique's SERVICE_PCT service charge is
// added, and the bag and checkout add it as its own line. The switch is per
// device, so the shop tablet can stay in showroom while the owner's phone
// shows costs.
//
// Leaving showroom asks for the boutique's 4-digit PIN when one is set, so
// a client at the counter cannot open the costs (agreed 2026-10-29). The
// PIN is kept as a hash on Users/{key}. Showroom is remembered for the
// account on this device, so signing out turns it off: that is the way back
// if the PIN is forgotten.

const KEY = "mv-showroom";
const EVENT = "mv-showroom";
const PIN_EVENT = "mv-showroom-pin";
let lastKey = "";

// On for this account on this device ("1" is from before it was per account).
const readOn = (key) => {
    try { const v = localStorage.getItem(KEY); return !!key && (v === key || v === "1"); } catch (e) { return false; }
};

export function setShowroom(on) {
    try { localStorage.setItem(KEY, on ? (lastKey || "1") : "0"); } catch (e) { /* private mode */ }
    window.dispatchEvent(new Event(EVENT));
}

// The PIN's hash, live from Users/{key}: one listener for the whole page.
let pinState = { key: "", hash: "" };
let pinFor = null;
let pinStop = () => {};
const pinSubs = new Set();
const watchPin = (key) => {
    if (pinFor === key) return;
    pinStop(); pinFor = key;
    const set = (next) => { pinState = next; pinSubs.forEach((fn) => fn(next)); };
    if (!key) { set({ key: "", hash: "" }); pinStop = () => {}; return; }
    pinStop = db.collection("Users").doc(key).onSnapshot(
        (snap) => set({ key, hash: (snap.exists && snap.data().showroomPin) || "" }),
        () => set({ key, hash: "" }),
    );
};
export function useShowroomPin(enabled = true) {
    const { key } = useBusiness();
    const [st, setSt] = useState(pinState);
    useEffect(() => { pinSubs.add(setSt); return () => { pinSubs.delete(setSt); }; }, []);
    useEffect(() => { if (enabled && key) watchPin(key); }, [enabled, key]);
    return { key, hasPin: !!key && st.key === key && !!st.hash, hash: st.key === key ? st.hash : "" };
}
export const hashPin = async (key, pin) => {
    const data = new TextEncoder().encode(`${key}|${pin}`);
    const buf = await window.crypto.subtle.digest("SHA-256", data);
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
};
export const saveShowroomPin = async (key, pin) => db.collection("Users").doc(key)
    .set({ showroomPin: pin ? await hashPin(key, pin) : "" }, { merge: true });

// Leave showroom: straight away, or after the PIN when one is set.
export function leaveShowroom() {
    if (pinState.hash && pinState.key === lastKey) window.dispatchEvent(new Event(PIN_EVENT));
    else setShowroom(false);
}

export function useShowroom() {
    const { biz, key, loaded } = useBusiness();
    const isBiz = isVerifiedBusiness(biz);
    if (key) lastKey = key;
    const [on, setOn] = useState(() => readOn(key));
    useEffect(() => {
        const sync = () => setOn(readOn(key));
        sync();
        window.addEventListener(EVENT, sync);
        window.addEventListener("storage", sync);
        return () => { window.removeEventListener(EVENT, sync); window.removeEventListener("storage", sync); };
    }, [key]);
    return { biz, accountKey: key, loaded, isBiz, showroom: isBiz && on, servicePct: SERVICE_PCT, setShowroom, leaveShowroom };
}

// The shop's name as the website shows it: the admin's spelling when set,
// otherwise the name they applied with ("fashion house" reads "Fashion House").
export const shopName = (biz) => {
    const raw = String((biz && (biz.displayName || biz.businessName)) || "").trim();
    if (!raw || (biz && biz.displayName)) return raw;
    return raw === raw.toLowerCase() ? raw.replace(/\b\p{L}/gu, (m) => m.toUpperCase()) : raw;
};

// Asks for the PIN to leave showroom; mounted once, in the nav bar.
export function ShowroomPinHost() {
    const { isBiz, accountKey } = useShowroom();
    const { hash } = useShowroomPin(isBiz);
    const [open, setOpen] = useState(false);
    const [pin, setPin] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        const ask = () => { setPin(""); setError(""); setOpen(true); };
        window.addEventListener(PIN_EVENT, ask);
        return () => window.removeEventListener(PIN_EVENT, ask);
    }, []);
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);
    if (!open) return null;
    const check = async (value) => {
        if (value.length !== 4) return;
        setBusy(true);
        try {
            if ((await hashPin(accountKey, value)) === hash) { setOpen(false); setShowroom(false); return; }
            setError("That PIN is not right.");
            setPin("");
        } finally { setBusy(false); }
    };
    return createPortal(
        <div className="bz-pin-wrap" onClick={() => setOpen(false)}>
            <div className="bz-pin" role="dialog" aria-modal="true" aria-labelledby="bz-pin-title" onClick={(e) => e.stopPropagation()}>
                <p className="bz-pin__eyebrow">Showroom</p>
                <h2 id="bz-pin-title">Enter your <em>PIN</em></h2>
                <p className="bz-pin__lead">To see your costs on this device.</p>
                <input className="bz-pin__input" type="password" inputMode="numeric" autoComplete="off" maxLength={4} autoFocus
                    value={pin} disabled={busy} aria-label="4-digit PIN" aria-invalid={!!error}
                    onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 4); setPin(v); setError(""); check(v); }} />
                {error && <p className="bz-pin__error" role="alert">{error}</p>}
                <button type="button" className="bz-pin__cancel" onClick={() => setOpen(false)}>Stay in showroom</button>
                <p className="bz-pin__hint">Forgot it? Log out and sign in again on this device; showroom turns off.</p>
            </div>
        </div>,
        document.body,
    );
}

// Set, change or remove the PIN (Settings, only outside showroom).
export function ShowroomPinSetter() {
    const { key, hasPin } = useShowroomPin(true);
    const [editing, setEditing] = useState(false);
    const [pin, setPin] = useState("");
    const [again, setAgain] = useState("");
    const [msg, setMsg] = useState("");
    const save = async () => {
        if (!/^\d{4}$/.test(pin)) { setMsg("Use 4 digits."); return; }
        if (pin !== again) { setMsg("The two PINs are not the same."); return; }
        try { await saveShowroomPin(key, pin); setEditing(false); setPin(""); setAgain(""); setMsg("PIN saved."); } catch (e) { setMsg("Could not save the PIN. Please try again."); }
    };
    const remove = async () => {
        try { await saveShowroomPin(key, ""); setMsg("PIN removed."); } catch (e) { setMsg("Could not remove the PIN."); }
    };
    const digits = (set) => (e) => { set(e.target.value.replace(/\D/g, "").slice(0, 4)); setMsg(""); };
    return (
        <div className="bz-pinset">
            <div className="bz-pinset__row">
                <span><b>Showroom PIN</b><small>{hasPin ? "Set. Leaving showroom asks for it." : "Set one so a client cannot switch to your costs."}</small></span>
                {!editing && <button type="button" onClick={() => { setEditing(true); setMsg(""); }}>{hasPin ? "Change" : "Set a PIN"}</button>}
                {!editing && hasPin && <button type="button" className="is-quiet" onClick={remove}>Remove</button>}
            </div>
            {editing && (
                <div className="bz-pinset__form">
                    <input type="password" inputMode="numeric" maxLength={4} value={pin} onChange={digits(setPin)} placeholder="New PIN" aria-label="New 4-digit PIN" autoComplete="new-password" />
                    <input type="password" inputMode="numeric" maxLength={4} value={again} onChange={digits(setAgain)} placeholder="Again" aria-label="The PIN again" autoComplete="new-password" />
                    <button type="button" onClick={save}>Save</button>
                    <button type="button" className="is-quiet" onClick={() => { setEditing(false); setPin(""); setAgain(""); setMsg(""); }}>Cancel</button>
                </div>
            )}
            {msg && <p className="bz-pinset__msg" role="status">{msg}</p>}
        </div>
    );
}

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

// The line under a price in showroom mode, in rupees so no one has to work
// it out: "₹10,349 with 15% service" (compact, on cards), or with the
// charge itself spelt out: "+ ₹1,350 service (15%) · ₹10,349 in all".
export function ServiceNote({ amount, compact = false, className = "" }) {
    return (
        <small className={`mv-service-note ${className}`.trim()}>
            {compact
                ? <><b>{inr(clientTotal(amount))}</b> with {SERVICE_PCT}% service</>
                : <>+ {inr(serviceCharge(amount))} service ({SERVICE_PCT}%) · <b>{inr(clientTotal(amount))} in all</b></>}
        </small>
    );
}

// The sum on a product page, read left to right: design + service = total.
export function ServiceBill({ amount }) {
    return (
        <div className="mv-bill" role="group" aria-label={`Design ${inr(amount)} plus ${SERVICE_PCT}% service ${inr(serviceCharge(amount))}, you pay ${inr(clientTotal(amount))}`}>
            <div className="mv-bill__cell"><b>{inr(amount)}</b><span>Design</span></div>
            <span className="mv-bill__op" aria-hidden="true">+</span>
            <div className="mv-bill__cell"><b>{inr(serviceCharge(amount))}</b><span>Service {SERVICE_PCT}%</span></div>
            <span className="mv-bill__op" aria-hidden="true">=</span>
            <div className="mv-bill__cell is-total"><b>{inr(clientTotal(amount))}</b><span>You pay</span></div>
        </div>
    );
}
